import { Button, Checkbox, Notification } from '@schibsted-svp/react-ui';
import { useEffect } from 'react';
import type { Asset } from '@schibsted-svp/svp-api-types';
import type { UseFormReturn } from 'react-hook-form';
import { getAccess, isVod } from 'models/asset';
import { useEnableDrmMutation } from 'services/admin-bff-sdk/generated';
import type { FormValues } from '../types';
import css from './VodDrmProtection.module.scss';
import { getNewsroomConfig } from '../../../config';

function hasDrmEnabled(asset: Asset): boolean {
    return Boolean(asset.additional?.metadata?.drmPlaybackUrl && asset.additional?.metadata?.drmProfile);
}

function hasPaywall(asset: Asset): boolean {
    return getAccess(asset).length > 0;
}

export function shouldShowVodDrmProtection(asset: Asset): boolean {
    const { features } = getNewsroomConfig(asset.provider);

    return features.drmVodEncryption.enabled && isVod(asset) && hasPaywall(asset);
}

type VodDrmProtectionProps = {
    asset: Asset;
    disabled?: boolean;
    register: UseFormReturn<FormValues>['register'];
};

export function VodDrmProtection({ asset, disabled, register }: VodDrmProtectionProps) {
    const [enableDrm, { isLoading, error }] = useEnableDrmMutation();

    const drmEnabled = hasDrmEnabled(asset);

    useEffect(() => {
        if (error) {
            Notification.notify.error('Failed to enable DRM protection');
        }
    }, [error]);

    const handleEnableDrm = () => {
        enableDrm({ provider: asset.provider, assetId: asset.id });
    };

    return (
        <div className={css.container}>
            <div className={css.statusRow}>
                <span className={css.label}>DRM protection</span>
                <span className={drmEnabled ? css.statusEnabled : css.statusDisabled}>
                    {drmEnabled ? 'Enabled' : 'Disabled'}
                </span>
            </div>
            {!drmEnabled && (
                <Button
                    type="button"
                    size="small"
                    variant="standard"
                    loading={isLoading}
                    disabled={isLoading || disabled}
                    onClick={handleEnableDrm}
                >
                    Enable DRM
                </Button>
            )}
            {drmEnabled && (
                <Checkbox
                    {...register('additional.metadata.drmPlaybackEnabled')}
                    label="Use DRM stream for playback"
                    disabled={disabled}
                />
            )}
        </div>
    );
}
